import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, Lock, Unlock, Upload, CalendarClock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AdminTitle, DataTable } from "@/pages/admin/AdminLayout";
import { Spinner } from "@/components/site/Bits";
import { useApi, errMsg, fileUrl, fmtDate, fmtPrice } from "@/lib/api";

const EMPTY = { title: "", slug: "", category: "", tagline: "", short_description: "", description: "", objectives: [], contents: [], skills: [], features: [], audience: [],
  price: 0, date: "", time: "18:00", duration_label: "", mode: "أونلاين عبر Google Meet", seats_total: 20, image: "", meet_link: "", meet_time: "", published: true, registration_open: true, featured: false };
const LISTS = [["objectives", "الأهداف التعليمية"], ["contents", "محتويات الدورة"], ["skills", "المهارات المكتسبة"], ["features", "مميزات الدورة"], ["audience", "الفئة المستهدفة"]];

export const useUpload = () => {
  const api = useApi();
  return useCallback(async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await api("post", "/admin/upload", fd)).url;
  }, [api]);
};

const F = ({ label, children, className = "" }) => <div className={`space-y-1.5 ${className}`}><Label className="text-xs">{label}</Label>{children}</div>;

const CourseForm = ({ initial, onClose, onSaved }) => {
  const api = useApi();
  const upload = useUpload();
  const [c, setC] = useState({ ...EMPTY, ...initial });
  const [busy, setBusy] = useState(false);
  const [withPdf, setWithPdf] = useState(!!initial?.material);
  const [pdf, setPdf] = useState(null);
  const [notifyStudents, setNotifyStudents] = useState(true);
  const set = (k, num) => (e) => setC({ ...c, [k]: num ? Number(e.target.value) : e.target.value });
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { setC({ ...c, image: await upload(file) }); toast.success("تم رفع الصورة"); } catch (err) { toast.error(errMsg(err)); }
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { id, seats_taken, seats_left, created_at, material, ...body } = c;
      const saved = await api(id ? "put" : "post", id ? `/admin/courses/${id}` : "/admin/courses", body);
      if (!id) setC((prev) => ({ ...prev, id: saved.id, slug: saved.slug }));
      if (withPdf && pdf) {
        const fd = new FormData();
        fd.append("file", pdf);
        await api("post", `/admin/courses/${saved.id}/material?notify_students=${!!id && notifyStudents}`, fd);
      } else if (!withPdf && material) {
        await api("delete", `/admin/courses/${saved.id}/material`);
      }
      toast.success(id ? "تم تعديل الدورة" : "تمت إضافة الدورة");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="course-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-right">{c.id ? "تعديل دورة" : "إضافة دورة جديدة"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
          <F label="اسم الدورة" className="md:col-span-2"><Input required value={c.title} onChange={set("title")} data-testid="cf-title" /></F>
          <F label="المجال / التصنيف"><Input value={c.category} onChange={set("category")} data-testid="cf-category" /></F>
          <F label="الرابط المختصر (اختياري)"><Input dir="ltr" value={c.slug} onChange={set("slug")} data-testid="cf-slug" /></F>
          <F label="العبارة الإعلانية" className="md:col-span-2"><Input value={c.tagline} onChange={set("tagline")} data-testid="cf-tagline" /></F>
          <F label="وصف مختصر" className="md:col-span-2"><Textarea rows={2} value={c.short_description} onChange={set("short_description")} data-testid="cf-short" /></F>
          <F label="الوصف الكامل" className="md:col-span-2"><Textarea rows={4} value={c.description} onChange={set("description")} data-testid="cf-description" /></F>
          {LISTS.map(([k, l]) => (
            <F key={k} label={`${l} (عنصر في كل سطر)`}><Textarea rows={4} value={c[k].join("\n")} onChange={(e) => setC({ ...c, [k]: e.target.value.split("\n") })} data-testid={`cf-${k}`} /></F>
          ))}
          <F label="السعر (ر.ع.)"><Input type="number" step="0.001" min="0" required value={c.price} onChange={set("price", true)} data-testid="cf-price" /></F>
          <F label="عدد المقاعد"><Input type="number" min="1" required value={c.seats_total} onChange={set("seats_total", true)} data-testid="cf-seats" /></F>
          <F label="التاريخ"><Input type="date" required value={c.date} onChange={set("date")} data-testid="cf-date" /></F>
          <F label="الوقت"><Input type="time" required value={c.time} onChange={set("time")} data-testid="cf-time" /></F>
          <F label="المدة"><Input value={c.duration_label} onChange={set("duration_label")} placeholder="ساعة ونصف" data-testid="cf-duration" /></F>
          <F label="طريقة التدريب"><Input value={c.mode} onChange={set("mode")} data-testid="cf-mode" /></F>
          <F label="رابط Google Meet"><Input dir="ltr" value={c.meet_link} onChange={set("meet_link")} placeholder="https://meet.google.com/..." data-testid="cf-meet" /></F>
          <F label="صورة الدورة">
            <div className="flex items-center gap-3">
              {c.image && <img src={fileUrl(c.image)} alt="" className="w-16 h-12 rounded object-cover" />}
              <label className="btn-gold-outline !py-2 !px-4 text-sm cursor-pointer"><Upload size={15} />رفع صورة<input type="file" accept="image/*" hidden onChange={onFile} data-testid="cf-image-upload" /></label>
            </div>
          </F>
          <div className="md:col-span-2 rounded-xl border border-[var(--line)] p-4 space-y-3" data-testid="cf-pdf-section">
            <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={withPdf} onCheckedChange={setWithPdf} data-testid="cf-pdf-toggle" />إرفاق ملف PDF يصل للمتدرب بعد الدفع (اختياري)</label>
            {withPdf && (
              <div className="space-y-2 text-sm">
                {c.material && !pdf && <p data-testid="cf-pdf-current">الملف الحالي: <b dir="ltr">{c.material.filename}</b> ({c.material.size < 1024 * 1024 ? `${Math.max(1, Math.round(c.material.size / 1024))} ك.ب` : `${(c.material.size / 1024 / 1024).toFixed(2)} م.ب`})</p>}
                <label className="btn-gold-outline !py-2 !px-4 text-sm cursor-pointer w-fit"><Upload size={15} />{c.material || pdf ? "استبدال الملف" : "اختيار ملف PDF"}
                  <input type="file" accept="application/pdf" hidden onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && f.size > 10 * 1024 * 1024) { toast.error("حجم الملف يجب ألا يتجاوز 10 ميجابايت"); return; }
                    setPdf(f || null);
                  }} data-testid="cf-pdf-input" />
                </label>
                {pdf && <p className="text-emerald-700" data-testid="cf-pdf-selected">سيتم رفع: <b dir="ltr">{pdf.name}</b> عند الحفظ</p>}
                {c.id && pdf && (
                  <label className="flex items-center gap-2 text-xs"><Switch checked={notifyStudents} onCheckedChange={setNotifyStudents} data-testid="cf-pdf-notify" />إشعار المتدربين الذين دفعوا مسبقاً بالبريد</label>
                )}
                <p className="text-xs text-[var(--muted-ink)]">يظهر الملف في حساب المتدرب بعد تأكيد الدفع، ويصله بريد بأن الملف متاح. الحد الأقصى 10 ميجابايت.</p>
              </div>
            )}
            {!withPdf && c.material && <p className="text-xs text-red-600" data-testid="cf-pdf-remove-warning">سيتم حذف الملف الحالي عند الحفظ</p>}
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-6 py-2">
            {[["published", "منشورة"], ["registration_open", "التسجيل مفتوح"], ["featured", "دورة مميزة"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm"><Switch checked={c[k]} onCheckedChange={(v) => setC({ ...c, [k]: v })} data-testid={`cf-${k}`} />{l}</label>
            ))}
          </div>
          <button disabled={busy} className="btn-copper md:col-span-2" data-testid="cf-save-btn">{busy ? "جارٍ الحفظ..." : "حفظ الدورة"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PostponeDialog = ({ course, onClose, onDone }) => {
  const api = useApi();
  const [f, setF] = useState({ date: course.date, time: course.time, reason: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api("post", `/admin/courses/${course.id}/postpone`, f);
      toast.success(`تم تأجيل الدورة وجارٍ إرسال البريد إلى ${r.notified} مشترك`);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" dir="rtl" data-testid="postpone-dialog">
        <DialogHeader><DialogTitle className="font-display">تأجيل موعد الدورة</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-[var(--muted-ink)]">{course.title} — الموعد الحالي: {fmtDate(course.date)} {course.time}</p>
          <div className="grid grid-cols-2 gap-3">
            <F label="التاريخ الجديد"><Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="postpone-date" /></F>
            <F label="الوقت الجديد"><Input type="time" required value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="postpone-time" /></F>
          </div>
          <F label="سبب التأجيل (اختياري)"><Input value={f.reason} maxLength={300} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="postpone-reason" /></F>
          <p className="text-xs text-[var(--muted-ink)]">سيصل بريد تلقائي بالموعد الجديد لكل المشتركين المؤكدين في الدورة.</p>
          <div className="flex gap-2">
            <button disabled={busy} className="btn-copper" data-testid="postpone-submit"><CalendarClock size={16} />{busy ? "جارٍ التأجيل..." : "تأجيل وإرسال البريد"}</button>
            <button type="button" onClick={onClose} className="btn-gold-outline" data-testid="postpone-cancel">إلغاء</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminCourses() {
  const api = useApi();
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);
  const [postpone, setPostpone] = useState(null);
  const load = useCallback(() => api("get", "/admin/courses").then(setList), [api]);
  useEffect(() => {
    load();
  }, [load]);
  const flag = async (c, k) => {
    try { await api("patch", `/admin/courses/${c.id}/flags`, { [k]: !c[k] }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`حذف الدورة "${c.title}"؟`)) return;
    try { await api("delete", `/admin/courses/${c.id}`); toast.success("تم حذف الدورة"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!list) return <Spinner />;
  return (
    <div data-testid="admin-courses">
      <AdminTitle title="إدارة الدورات"><button className="btn-copper !py-2.5" onClick={() => setEdit({})} data-testid="add-course-btn"><Plus size={18} />إضافة دورة</button></AdminTitle>
      <DataTable head={["الدورة", "التاريخ", "السعر", "المقاعد", "النشر", "التسجيل", "إجراءات"]} testId="admin-courses-table">
        {list.map((c) => (
          <tr key={c.id} data-testid={`admin-course-row-${c.id}`}>
            <td className="max-w-[280px]"><div className="flex items-center gap-3"><img src={fileUrl(c.image) || "/images/course-ai.jpg"} alt="" className="w-12 h-9 rounded object-cover" /><span className="truncate">{c.title}</span></div></td>
            <td className="whitespace-nowrap">{fmtDate(c.date)}</td><td className="whitespace-nowrap">{fmtPrice(c.price)}</td>
            <td>{c.seats_taken}/{c.seats_total}</td>
            <td><button onClick={() => flag(c, "published")} className="inline-flex items-center gap-1 text-xs" data-testid={`toggle-publish-${c.id}`}>{c.published ? <><Eye size={15} className="text-emerald-600" />منشورة</> : <><EyeOff size={15} />مخفية</>}</button></td>
            <td><button onClick={() => flag(c, "registration_open")} className="inline-flex items-center gap-1 text-xs" data-testid={`toggle-registration-${c.id}`}>{c.registration_open ? <><Unlock size={15} className="text-emerald-600" />مفتوح</> : <><Lock size={15} className="text-red-600" />موقوف</>}</button></td>
            <td className="whitespace-nowrap">
              <button onClick={() => setEdit(c)} className="p-2 hover:text-[var(--copper)]" data-testid={`edit-course-${c.id}`}><Pencil size={16} /></button>
              <button onClick={() => setPostpone(c)} className="p-2 hover:text-[var(--copper)]" title="تأجيل الموعد" data-testid={`postpone-course-${c.id}`}><CalendarClock size={16} /></button>
              <button onClick={() => del(c)} className="p-2 hover:text-red-600" data-testid={`delete-course-${c.id}`}><Trash2 size={16} /></button>
            </td>
          </tr>
        ))}
      </DataTable>
      {postpone && <PostponeDialog course={postpone} onClose={() => setPostpone(null)} onDone={() => { setPostpone(null); load(); }} />}
      {edit && <CourseForm initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
